import { DayResponse, TimeInSpaceEmployeeExitBeforeResponse } from "api/generated";
import { Loader } from "components/loader/Loader";
import React, { useEffect, useState } from "react";
import { getEmployeesWithExitBefore } from "redux/actions/statisticsActions";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getBuildings } from "redux/actions/buildingActions";
import { useDownloadExcel } from "react-export-table-to-excel";
import { TextField } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

export const StatisticsEarlyExits = () => {
  const dispatch = useAppDispatch();
  const tableRef = React.useRef(null);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [targetLocalTime, setTargetLocalTime] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<TimeInSpaceEmployeeExitBeforeResponse[]>([]);
  const dates = reports[0]?.data?.map((dayReport) => dayReport.date) ?? [];

  const { buildings, buildingsLoadingStatus } = useAppSelector((state) => state.building);
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const selectedBuilding = buildings.find((b) => b.id === buildingId);

  const navigate = useNavigate();

  const handleNavigate = () => {
  navigate("/statistics");
  };

  useEffect(() => {
    if (buildingsLoadingStatus === "Loading" || buildingsLoadingStatus === "Loaded") return;

    dispatch(getBuildings());
  }, [dispatch, buildingsLoadingStatus]);

  const canLoad = !!buildingId && targetLocalTime && endDate >= startDate;
  const loadData = () => {
    if (!canLoad) return;

    setIsLoading(true);
    dispatch(getEmployeesWithExitBefore({ buildingId, startDate, endDate, targetLocalTime }))
      .unwrap()
      .then(setReports)
      .finally(() => setIsLoading(false));
  };

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: `${format(startDate, "yyyy-MM-dd")} - ${format(
      endDate,
      "yyyy-MM-dd",
    )} -- ${targetLocalTime} - Exit report - ${selectedBuilding?.name}`,
    sheet: "Employees",
  });

  const renderDayResponse = (day: DayResponse, employeeId: number) => {
    const getContent = () => {
      switch (day.status) {
        case "NO_ENTRY":
          return "No entry";
        case "STILL_INSIDE":
          return "Still inside";
        case "EXIT_AFTER":
          return "Exit after";
        case undefined:
          return "/";
        default:
          const time = format(parseISO(day.exitDateTime ?? ""), "HH:mm");
          return day.timeInSpace ? `${day.timeInSpace} (exit ${time})` : "-";
      }
    };
    const getBackgroundColor = () => {
      switch (day.status) {
        case "EXIT_BEFORE_BELOW_BARE_MIN":
          return "rgb(237, 24, 13, 0.5)";
        case "EXIT_BEFORE_BELOW_MIN":
          return "rgba(255, 255, 0, 0.4)";
        case "NO_ENTRY":
        case "STILL_INSIDE":
        case "EXIT_AFTER":
        case "EXIT_BEFORE":
        case undefined:
          return undefined;
      }
    };

    return (
      <td key={`${employeeId}-${day.date}`} className="table__cell" style={{ backgroundColor: getBackgroundColor() }}>
        {getContent()}
      </td>
    );
  };

  return (
    <main className="main">
      <div className="wrap">
        <div className="intro intro--stats">
          <button type="button" onClick={handleNavigate} className="link link--icon link--arrow-left">
            Back
          </button>
          <h1 className="intro__title">Statistics by <span className="intro__title-highlight">ACS - early exits</span></h1>
        </div>
        <div className="manager">
          {isLoading && <Loader />}

          <h2 className="manager__title manager__title--compact">
            <span className="manager__title-text">Inputs</span>

            <TextField
              className="manager__title-select"
              type="date"
              value={format(startDate, "yyyy-MM-dd")}
              onChange={(evt) => setStartDate(parseISO(evt.target.value))}
            />
            <TextField
              className="manager__title-select"
              type="date"
              value={format(endDate, "yyyy-MM-dd")}
              onChange={(evt) => setEndDate(parseISO(evt.target.value))}
            />
            <TextField
              className="manager__title-select"
              type="time"
              value={targetLocalTime}
              onChange={(evt) => setTargetLocalTime(evt.target.value)}
            />

            <select
              className="manager__title-select"
              value={buildingId ?? 0}
              onChange={(e) => setBuildingId(parseInt(e.target.value))}>
              <option value={0}>Select a building</option>
              {buildings.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <div className="manager__actions">
              <button
                onClick={loadData}
                disabled={!canLoad}
                id="btn-load"
                type="button"
                className="btn">
                Load
              </button>

              <button
                onClick={onDownload}
                id="btn-export"
                type="button"
                disabled={reports.length === 0}
                className="link link--icon link--export">
                Export
              </button>
            </div>
          </h2>

          <div style={{ height: "calc(100vh - 280px)", overflow: "auto" }}>
            <div className="manager__content">
              <table className="table" ref={tableRef}>
                <thead>
                  <tr className="table__row ">
                    <th className="table__head">Employee</th>
                    <th className="table__head">Token</th>
                    {dates.map((d) => (
                      <th key={d} className="table__head">
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reports.map((item) => {
                    return (
                      <tr className="table__row alternate-table-row" key={item.employeeId ?? "-"}>
                        <td className="table__cell">{item.employeeFullName ?? "-"}</td>
                        <td className="table__cell">{item.employeeCardNumber ?? "-"}</td>

                        {item.data?.map((d) => renderDayResponse(d, item.employeeId!))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
